/* eslint-disable @typescript-eslint/explicit-function-return-type */
import { getEnv } from '@utils/env'

const period = {
  periodStart: '2026-01-01',
  periodEnd: '2026-06-30',
  bucket: 'month',
}

const labels = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun']

const scalar = (value: number) => ({ ...period, value })
const series = (values: number[], customLabels = labels) => ({
  ...period,
  buckets: values.map((value, index) => ({
    label: customLabels[index] ?? `Period ${index + 1}`,
    value,
  })),
})
const kpiSeries = (value: number, values: number[]) => ({
  ...scalar(value),
  ...series(values),
})

const reportsByPage: Record<string, unknown> = {
  '/finance/revenue-realization-analytics': {
    totalProduction: scalar(628400),
    totalCollections: scalar(584900),
    netCollection: scalar(93.1),
    grossCollection: scalar(89.4),
    sameDayCollections: scalar(142600),
    adjustmentRate: scalar(4.8),
    production: kpiSeries(
      628400,
      [89000, 95500, 101200, 108400, 112300, 122000],
    ),
    collection: kpiSeries(
      584900,
      [81000, 88700, 96800, 101400, 105300, 111700],
    ),
    collectionForProductionOfPeriod: kpiSeries(
      552300,
      [78000, 84400, 91200, 95700, 99100, 103900],
    ),
    netCollectionPercentByPeriod: series([91, 92, 95, 94, 93, 94]),
    adjustmentRateByPeriod: series([5.8, 5.2, 4.9, 4.6, 4.3, 4.1]),
    insuranceVsPatient: {
      ...period,
      insurance: { amount: 368500, percentage: 63 },
      patient: { amount: 216400, percentage: 37 },
    },
  },
  '/finance/goal-attainment-variance': {
    totalProduction: scalar(628400),
    totalCollections: scalar(584900),
    productionByPeriod: series([89000, 95500, 101200, 108400, 112300, 122000]),
    collectionByPeriod: series([81000, 88700, 96800, 101400, 105300, 111700]),
    productionGoalByPeriod: series([
      92000, 96000, 100000, 104000, 108000, 112000,
    ]),
    collectionGoalByPeriod: series([
      85000, 89000, 93000, 97000, 101000, 105000,
    ]),
    dailyRunRateVsPace: {
      ...period,
      runRate: 5740,
      requiredPace: 5200,
      gap: 540,
      workdaysElapsed: 14,
      totalWorkdays: 21,
    },
  },
  '/finance/receivables-risk-analysis': {
    totalAr: scalar(284300),
    insuranceAr: scalar(176200),
    patientAr: scalar(108100),
    arOver90: scalar(42700),
    averageDaysInAr: scalar(31),
    arTrendByPeriod: series([312000, 306000, 298000, 293000, 288000, 284300]),
    arAgingBuckets: series(
      [132000, 65400, 44200, 42700],
      ['0–30', '31–60', '61–90', '90+'],
    ),
    averageDaysInArByPeriod: series([38, 36, 35, 33, 32, 31]),
    highBalancePatients: {
      ...period,
      patients: [
        {
          patientId: 'P-1042',
          patientName: 'Jordan Lee',
          balance: 8420,
          daysOutstanding: 96,
        },
        {
          patientId: 'P-1188',
          patientName: 'Casey Morgan',
          balance: 6175,
          daysOutstanding: 72,
        },
        {
          patientId: 'P-1204',
          patientName: 'Alex Rivera',
          balance: 4890,
          daysOutstanding: 54,
        },
      ],
    },
  },
  '/finance/claims-performance': {
    totalClaimsSubmitted: scalar(1248),
    claimsPending: scalar(136),
    claimsOverdue: scalar(42),
    claimsDenied: scalar(31),
    claimsAcceptanceRate: scalar(94.6),
    resubmissionRate: scalar(3.8),
    averageDaysToPaymentByPeriod: series([25, 23, 22, 20, 19, 18]),
    claimsStatusOverview: series(
      [1039, 136, 42, 31],
      ['Paid', 'Pending', 'Overdue', 'Denied'],
    ),
    averageDaysToPaymentByCarrier: series(
      [15, 18, 21, 24],
      ['Delta', 'Aetna', 'Cigna', 'MetLife'],
    ),
    insuranceArAgingBuckets: series(
      [81200, 45200, 28100, 21700],
      ['0–30', '31–60', '61–90', '90+'],
    ),
  },
  '/patients/patient-base-health': {
    totalActivePatients: scalar(3842),
    totalActivePatientsFooter: scalar(126),
    netActiveGrowth: scalar(4.8),
    netActiveGrowthFooter: scalar(1.2),
    retentionRate: scalar(91.4),
    retentionRateFooter: scalar(2.1),
    reactivationRate: scalar(18.7),
    reactivationRateFooter: scalar(3.4),
    atRiskPatientCount: scalar(286),
    atRiskPatientCountFooter: scalar(-22),
    patientPopulationTrend: {
      ...period,
      buckets: labels.map((label, index) => ({
        label,
        active: 3500 + index * 65,
        dormant: 410 - index * 18,
        lost: 120 - index * 7,
      })),
    },
    lastVisitDistribution: series(
      [1840, 1020, 612, 370],
      ['0–6 months', '7–12 months', '13–18 months', '18+ months'],
    ),
    atRiskByAge: {
      ...period,
      buckets: [
        { label: '0–17', value: 38, percentage: 13 },
        { label: '18–34', value: 72, percentage: 25 },
        { label: '35–54', value: 101, percentage: 35 },
        { label: '55+', value: 75, percentage: 27 },
      ],
    },
    atRiskPatients: {
      ...period,
      summary: { high: 54, medium: 98, low: 134, revenueAtRisk: 186400 },
      patients: [
        {
          patientId: 'P-1008',
          patientName: 'Taylor Brooks',
          lastVisitDaysAgo: 430,
          unscheduledTreatment: 4200,
          noShowCount: 2,
          outstandingBalance: 1260,
          riskScore: 91,
          riskLevel: 'High',
        },
        {
          patientId: 'P-1114',
          patientName: 'Morgan Chen',
          lastVisitDaysAgo: 318,
          unscheduledTreatment: 2800,
          noShowCount: 1,
          outstandingBalance: 840,
          riskScore: 74,
          riskLevel: 'Medium',
        },
      ],
    },
  },
  '/patients/patient-value-rev-distribution': {
    averagePatientValue: scalar(1834),
    averagePatientValueFooter: scalar(126),
    activePatientRevenue: scalar(7047200),
    activePatientRevenueFooter: scalar(8.2),
    unscheduledTreatment: scalar(486300),
    unscheduledTreatmentFooter: scalar(-4.1),
    totalOutstandingAr: scalar(284300),
    totalOutstandingArFooter: scalar(-7.4),
    productionByProcedureMix: {
      ...period,
      total: 628400,
      categories: [
        {
          id: 'restorative',
          label: 'Restorative',
          amount: 224000,
          percentage: 35.6,
        },
        {
          id: 'preventive',
          label: 'Preventive',
          amount: 151000,
          percentage: 24,
        },
        { id: 'cosmetic', label: 'Cosmetic', amount: 138400, percentage: 22 },
        { id: 'surgery', label: 'Surgery', amount: 115000, percentage: 18.4 },
      ],
    },
    activePatientValueSegments: {
      ...period,
      segments: [
        { id: 'high', label: 'High value', count: 768, percentage: 20 },
        { id: 'core', label: 'Core', count: 2113, percentage: 55 },
        {
          id: 'growth',
          label: 'Growth opportunity',
          count: 961,
          percentage: 25,
        },
      ],
      highValueRevenueShare: 48,
    },
    newVsExistingProduction: {
      ...period,
      rows: [
        { label: 'New Patient', patients: 384, revenue: 168000 },
        { label: 'Existing Patient', patients: 3458, revenue: 460400 },
      ],
    },
    arUnscheduledByProvider: {
      ...period,
      rows: [
        {
          providerId: 'D-1',
          providerName: 'Dr. Patel',
          patients: 74,
          unscheduledValue: 128400,
          outstandingAr: 82400,
        },
        {
          providerId: 'D-2',
          providerName: 'Dr. Kim',
          patients: 62,
          unscheduledValue: 104800,
          outstandingAr: 69100,
        },
      ],
    },
  },
  '/patients/patient-risk-compliance': {
    patientsWithUnscheduledTreatments: scalar(286),
    patientsWithUnscheduledTreatmentsFooter: scalar(-18),
    patientsWithMedicalAlerts: scalar(418),
    patientsWithMedicalAlertsFooter: scalar(12),
    patientsOverdue: scalar(193),
    patientsOverdueFooter: scalar(-24),
    unscheduledByProductionBucket: {
      ...period,
      buckets: [
        { label: 'Under $1k', patients: 126, revenue: 72400 },
        { label: '$1k–$3k', patients: 92, revenue: 168000 },
        { label: '$3k+', patients: 68, revenue: 245900 },
      ],
    },
    overdueVisitGrouping: {
      ...period,
      buckets: [
        { label: '1–30 days', count: 88, percentage: 46 },
        { label: '31–90 days', count: 63, percentage: 33 },
        { label: '90+ days', count: 42, percentage: 21 },
      ],
    },
    medicalAlertCategories: {
      ...period,
      rows: [
        { id: 'allergy', category: 'Allergies', patients: 184 },
        { id: 'cardiac', category: 'Cardiac', patients: 112 },
        { id: 'diabetes', category: 'Diabetes', patients: 78 },
      ],
    },
  },
  '/patients/family-opportunity-intelligence': {
    sameFamilyOpportunities: scalar(142),
    sameFamilyOpportunitiesFooter: scalar(18),
    householdsWithGaps: scalar(86),
    householdsWithGapsFooter: scalar(-9),
    upcomingBirthdays: scalar(64),
    upcomingBirthdaysFooter: scalar(12),
    multiFamilySchedulingGaps: scalar(118),
    multiFamilySchedulingGapsFooter: scalar(-14),
    sameFamilyByCategory: {
      ...period,
      buckets: [
        { label: 'Hygiene', count: 68, percentage: 48 },
        { label: 'Treatment', count: 44, percentage: 31 },
        { label: 'Recall', count: 30, percentage: 21 },
      ],
    },
    multiFamilyGapBySpread: {
      ...period,
      buckets: [
        { label: '1 member', count: 52, percentage: 44 },
        { label: '2 members', count: 41, percentage: 35 },
        { label: '3+ members', count: 25, percentage: 21 },
      ],
    },
    birthdayMilestones: {
      ...period,
      rows: [
        {
          id: 'B-1',
          patientName: 'Jamie Cruz',
          date: '2026-07-08',
          household: 'Cruz family',
        },
        {
          id: 'B-2',
          patientName: 'Avery Smith',
          date: '2026-07-14',
          household: 'Smith family',
        },
      ],
    },
    householdSchedulingGaps: {
      ...period,
      rows: [
        {
          id: 'H-1',
          household: 'Garcia family',
          members: 'Maria, Luis, Sofia',
          dueUnscheduled: '2 hygiene, 1 recall',
        },
        {
          id: 'H-2',
          household: 'Wilson family',
          members: 'Noah, Emma',
          dueUnscheduled: '1 treatment',
        },
      ],
    },
  },
  '/engagement/recall-reactivation-control': {
    recallCompliance: scalar(87.4),
    reactivationRate: scalar(19.6),
    patientsDueForRecall: scalar(224),
    hygieneOverdue: scalar(146),
    recallComplianceTrend: series([79, 81, 83, 84, 86, 87.4]),
    reactivationTrend: {
      ...period,
      buckets: labels.map((label, index) => ({
        label,
        count: 24 + index * 5,
        percentage: 13 + index * 1.3,
      })),
    },
    providerRecallResponse: {
      ...period,
      rows: [
        {
          providerId: 'D-1',
          provider: 'Dr. Patel',
          eligible: 310,
          completed: 278,
          ratePct: 89.7,
        },
        {
          providerId: 'D-2',
          provider: 'Dr. Kim',
          eligible: 284,
          completed: 244,
          ratePct: 85.9,
        },
      ],
    },
    hygieneOverduePatients: {
      ...period,
      rows: [
        {
          patientId: 'P-1033',
          patientName: 'Robin Diaz',
          lastHygiene: '2025-07-12',
          daysSinceLastVisit: 374,
          phone: '(555) 010-1033',
        },
        {
          patientId: 'P-1190',
          patientName: 'Sam Nguyen',
          lastHygiene: '2025-09-04',
          daysSinceLastVisit: 320,
          phone: '(555) 010-1190',
        },
      ],
    },
  },
  '/engagement/schedule-reliability': {
    noShowRate: scalar(4.2),
    cancellationRate: scalar(7.8),
    noShowCount: scalar(68),
    repeatOffenders: scalar(14),
    noShowCancellationTrend: {
      ...period,
      buckets: labels.map((label, index) => ({
        label,
        noShowRate: 6.2 - index * 0.4,
        cancellationRate: 9.6 - index * 0.36,
      })),
    },
    repeatOffenderRows: {
      ...period,
      rows: [
        {
          patientId: 'P-1071',
          patientName: 'Chris Park',
          noShows: 3,
          cancellations: 2,
          lastEventDate: '2026-06-22',
        },
        {
          patientId: 'P-1147',
          patientName: 'Drew Hall',
          noShows: 2,
          cancellations: 3,
          lastEventDate: '2026-06-18',
        },
      ],
    },
    noShowPatientRows: {
      ...period,
      rows: [
        {
          appointmentId: 'A-2088',
          patientName: 'Chris Park',
          apptDate: '2026-06-22',
          provider: 'Dr. Patel',
          procedure: 'Crown prep',
          phone: '(555) 010-1071',
        },
        {
          appointmentId: 'A-2114',
          patientName: 'Drew Hall',
          apptDate: '2026-06-18',
          provider: 'Dr. Kim',
          procedure: 'Hygiene',
          phone: '(555) 010-1147',
        },
      ],
    },
    cancellationWindowDistribution: {
      ...period,
      buckets: [
        { label: 'Same day', count: 38, percentage: 32 },
        { label: '1 day', count: 47, percentage: 39 },
        { label: '2+ days', count: 35, percentage: 29 },
      ],
    },
  },
  '/engagement/treatment-conversion-funnel': {
    treatmentAcceptanceRate: scalar(71.8),
    unscheduledTreatmentValue: { ...scalar(486300), patientCount: 286 },
    examUnscheduledCases: scalar(74),
    completedVsDiagnosed: scalar(66.4),
    conversionFunnel: {
      ...period,
      stages: [
        { id: 'diagnosed', label: 'Diagnosed', count: 840, value: 1180000 },
        { id: 'presented', label: 'Presented', count: 772, value: 1060000 },
        { id: 'accepted', label: 'Accepted', count: 604, value: 846000 },
        { id: 'completed', label: 'Completed', count: 558, value: 741000 },
      ],
    },
    unscheduledOpportunities: {
      ...period,
      buckets: [
        { id: 'restorative', label: 'Restorative', count: 118, value: 184000 },
        { id: 'cosmetic', label: 'Cosmetic', count: 64, value: 143000 },
        { id: 'surgical', label: 'Surgical', count: 48, value: 121000 },
      ],
    },
    acceptanceRateTrend: series([64, 66, 67, 69, 70, 71.8]),
    examUnscheduledByProvider: {
      ...period,
      rows: [
        {
          providerId: 'D-1',
          provider: 'Dr. Patel',
          examDiagnoses: 188,
          unscheduledCount: 31,
          unscheduledPct: 16.5,
        },
        {
          providerId: 'D-2',
          provider: 'Dr. Kim',
          examDiagnoses: 171,
          unscheduledCount: 28,
          unscheduledPct: 16.4,
        },
      ],
    },
  },
  '/engagement/communication-center-performance': {
    totalCalls: scalar(2840),
    answered: scalar(2436),
    missed: scalar(286),
    declined: scalar(118),
    followUps: scalar(174),
    sentiment: { ...scalar(82), dominantSentiment: 'Positive' },
    avgHandlingTimeSeconds: scalar(214),
    callVolumeOutcomes: {
      ...period,
      buckets: labels.map((label, index) => ({
        label,
        total: 410 + index * 25,
        answered: 350 + index * 23,
        missed: 44 - index * 2,
      })),
    },
    messagingPerformance: {
      ...period,
      buckets: labels.map((label, index) => ({
        label,
        inbound: 220 + index * 18,
        replied: 198 + index * 17,
      })),
    },
    communicationEffectiveness: {
      ...period,
      bars: [
        {
          id: 'answer',
          label: 'Answer rate',
          labelLine1: 'Answer',
          labelLine2: 'rate',
          rate: 85.8,
        },
        {
          id: 'reply',
          label: 'Reply rate',
          labelLine1: 'Reply',
          labelLine2: 'rate',
          rate: 91.2,
        },
        {
          id: 'followup',
          label: 'Follow-up completion',
          labelLine1: 'Follow-up',
          labelLine2: 'completion',
          rate: 78.6,
        },
      ],
    },
  },
  '/operations/scheduled-appointments': {
    patientsSeen: scalar(1642),
    avgFilledSlots: scalar(38.4),
    avgOpenSlots: scalar(6.2),
    avgHygieneGaps: scalar(2.8),
    noShows: scalar(68),
    filledSlotsByPeriod: series([34, 35, 36, 37, 38, 39]),
    openSlotsByPeriod: series([9, 8, 8, 7, 7, 6]),
    hygieneGapsByPeriod: series([5, 4, 4, 3, 3, 2]),
    scheduledProductionByPeriod: series([
      96000, 101000, 108000, 113000, 119000, 126000,
    ]),
    productionTargetByPeriod: series([
      100000, 104000, 108000, 112000, 116000, 120000,
    ]),
    noShowsByPeriod: series([16, 14, 12, 11, 9, 6]),
  },
  '/operations/chair-room-utilization': {
    chairTimeUsed: scalar(1184),
    chairTimeAvailable: scalar(1460),
    avgRoomsUsed: scalar(4.4),
    avgRoomOperations: scalar(5.2),
    utilizationRate: scalar(81.1),
    chairTimeUsedByPeriod: series([168, 176, 188, 202, 216, 234]),
    chairTimeAvailableByPeriod: series([230, 235, 240, 245, 250, 260]),
    roomsUsedByPeriod: series([3.6, 3.9, 4.1, 4.4, 4.7, 4.9]),
    roomsAvailableByPeriod: series([5, 5, 5, 5, 6, 6]),
    providersWorkingByPeriod: series([3, 3, 4, 4, 4, 5]),
    utilizationRateByPeriod: series([73, 75, 78, 82, 86, 90]),
  },
  '/operations/hygiene-performance': {
    hygieneVisits: scalar(884),
    hygieneOpenings: scalar(42),
    hygieneUtilization: scalar(86.7),
    recallCompliance: scalar(87.4),
    hygieneReactivation: scalar(19.6),
    hygieneOpeningsByPeriod: series([62, 56, 51, 48, 45, 42]),
    hygieneUtilizationByPeriod: series([76, 78, 81, 83, 85, 86.7]),
    recallComplianceByPeriod: series([79, 81, 83, 84, 86, 87.4]),
    hygieneReactivationByPeriod: series([12, 14, 15, 17, 18, 19.6]),
  },
  '/provider-performance/production-performance': {
    production: kpiSeries(
      628400,
      [89000, 95500, 101200, 108400, 112300, 122000],
    ),
    productionGoal: kpiSeries(
      612000,
      [92000, 96000, 100000, 104000, 108000, 112000],
    ),
    avgProductionPerHour: kpiSeries(612, [540, 558, 576, 591, 603, 612]),
    avgProductionPerDay: kpiSeries(5740, [4680, 4920, 5100, 5340, 5520, 5740]),
  },
  '/provider-performance/collections-financial-efficiency': {
    collections: kpiSeries(
      584900,
      [81000, 88700, 96800, 101400, 105300, 111700],
    ),
    production: kpiSeries(
      628400,
      [89000, 95500, 101200, 108400, 112300, 122000],
    ),
    netCollectionPercentage: kpiSeries(93.1, [91, 92, 95, 94, 93, 94]),
    sameDayPayments: kpiSeries(
      142600,
      [18200, 20400, 22100, 24400, 27100, 30400],
    ),
  },
  '/provider-performance/clinical-mix-treatment-profile': {
    totalProcedures: kpiSeries(2846, [410, 432, 451, 472, 511, 570]),
    crownPercentage: kpiSeries(18.4, [15, 16, 17, 18, 18, 18.4]),
    srpPercentage: kpiSeries(12.8, [10, 11, 11.5, 12, 12.4, 12.8]),
    highValueProcedurePercentage: kpiSeries(31.6, [26, 27, 28, 29, 30, 31.6]),
    crownProcedures: kpiSeries(524, [70, 76, 82, 88, 98, 110]),
    srpProcedures: kpiSeries(364, [48, 52, 56, 62, 68, 78]),
    fillingProcedures: kpiSeries(846, [118, 126, 134, 142, 153, 173]),
    extractionProcedures: kpiSeries(218, [28, 31, 34, 37, 41, 47]),
  },
  '/provider-performance/treatment-effectiveness': {
    acceptanceRate: kpiSeries(71.8, [64, 66, 67, 69, 70, 71.8]),
    completedVsDiagnosedRate: kpiSeries(66.4, [58, 60, 61, 63, 65, 66.4]),
    totalDiagnosed: kpiSeries(
      1180000,
      [162000, 174000, 186000, 201000, 218000, 239000],
    ),
    pendingTreatments: kpiSeries(
      486300,
      [602000, 581000, 558000, 531000, 508000, 486300],
    ),
    statusDistribution: series(
      [558, 186, 96],
      ['Completed', 'Scheduled', 'Unscheduled'],
    ),
  },
  '/provider-performance/efficiency-scorecard': {
    efficiencyScore: kpiSeries(86.2, [78, 80, 82, 83, 85, 86.2]),
    productionPerHour: kpiSeries(612, [540, 558, 576, 591, 603, 612]),
    chairUtilization: kpiSeries(81.1, [73, 75, 78, 82, 86, 90]),
    totalProduction: kpiSeries(
      628400,
      [89000, 95500, 101200, 108400, 112300, 122000],
    ),
    providers: [
      {
        providerId: 'D-1',
        providerName: 'Dr. Patel',
        production: 284000,
        productionPerHour: 648,
        efficiencyScore: 91,
        chairUtilization: 88,
      },
      {
        providerId: 'D-2',
        providerName: 'Dr. Kim',
        production: 232000,
        productionPerHour: 594,
        efficiencyScore: 85,
        chairUtilization: 82,
      },
      {
        providerId: 'D-3',
        providerName: 'Dr. Rivera',
        production: 112400,
        productionPerHour: 531,
        efficiencyScore: 79,
        chairUtilization: 74,
      },
    ],
  },
}

/** Report fixtures are opt-in; absent or invalid configuration uses the API. */
export const isReportsMockDataEnabled = (): boolean =>
  getEnv('REACT_APP_USE_REPORTS_MOCKS').toLowerCase() === 'true'

export const getMockReportByPage = (pageId: string): unknown =>
  reportsByPage[pageId]
